<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import Trans from "~/modules/translations";
import router from "@/router";

import { Bell, DollarSign } from "lucide-vue-next";
import Discord from "/icon-discord.svg?url";
import Twitter from "/icon-twitter.svg?url";
import Button from "./formsV2/Button.vue";

import { useNotificationCenter } from "~/stores/storeNotificationCenter";

import { useStoreApp } from "~/stores/storeApp";
import { useStoreUserV2 } from "@/stores/storeUserV2";
import { useWeb3AuthStore } from "~/stores/storeWeb3auth";
import { roundToDecimals } from "~/utils/math";

const { t } = useI18n();

const notificationCenter = useNotificationCenter();
const storeApp = useStoreApp();
const storeUser = useStoreUserV2();

const handleShowDepositModal = () => {
  storeApp.showDepositModal = true;
};
</script>

<template>
  <div
    @click="storeUser.openProfileDrawer('default')"
    class="flex gap-1 rounded-full font-semibold"
    v-if="storeUser.isConnected"
  >
    <div
      class="text-xs flex items-center bg-primary-110 bg-opacity-50 rounded-full border border-transparent hover:cursor-pointer hover:bg-opacity-100 transition-colors text-neutral-200 hover:text-white mr-3"
    >
      <Tooltip :text="t('drawers.profile.tokenBalance')">
        <div class="px-2 gap-2 flex items-center">
          <icon-fg-fungiball-token class="w-f3 h-f3" />
          <p class="font-normal">
            {{ storeUser.tokenBalance }}
          </p>
        </div>
      </Tooltip>
      <template v-if="storeUser.creditBalance">
        <span class="text-neutral-700 font-light"> | </span>
        <Tooltip :text="t('drawers.profile.creditBalance')">
          <div class="px-2 gap-1 flex items-center">
            <DollarSign
              :size="16"
              class="mr-0.5 border px-[2px] rounded-full"
              :stroke-width="2"
              alt="Credits"
            />

            <p class="font-normal">
              {{ storeUser.creditBalance }}
            </p>
          </div>
        </Tooltip>
      </template>

      <span class="text-neutral-700 font-light"> | </span>

      <template v-if="storeUser.migrationToUSDCCompleted">
        <Tooltip :text="t('drawers.profile.usdcBalance')">
          <div
            @click.stop="handleShowDepositModal"
            class="gap-1 px-2 flex items-center rounded-full"
          >
            <icon-fgc-usdc class="w-4 h-4" />

            <p class="font-normal">
              {{ roundToDecimals(storeUser.usdcRemaining || 0, 2) }}
            </p>
          </div>
        </Tooltip>
      </template>
      <template v-else>
        <Tooltip :text="t('drawers.profile.wpolBalance')">
          <div
            @click.stop="handleShowDepositModal"
            class="px-2 gap-2 flex items-center rounded-full"
          >
            <icon-fgc-w-matic class="w-f3 h-f3" />

            <p class="font-normal">
              {{ roundToDecimals(storeUser.wpolRemaining || 0, 2) }}
            </p>
          </div>
        </Tooltip>
      </template>

      <span class="text-neutral-700 font-light"> | </span>

      <Tooltip
        :text="
          t('drawers.notifications.notificationCount', {
            count: notificationCenter.unreadNotificationsCount,
          })
        "
      >
        <div
          @click.stop="notificationCenter.open = true"
          class="px-2 gap-2 flex items-center rounded-full"
        >
          <div class="relative flex flex-row gap-0.5 items-center">
            <Bell class="w-f3 h-f3" />

            <div
              v-if="notificationCenter.unreadNotificationsCount > 0"
              class="bg-red-600 px-1 py-0.5 min-w-5 flex items-center justify-center rounded text-xs text-[10px] font-normal"
            >
              {{
                notificationCenter.unreadNotificationsCount > 9
                  ? "9+"
                  : notificationCenter.unreadNotificationsCount
              }}
            </div>
          </div>
        </div>
      </Tooltip>
      <span class="text-neutral-700 font-light"> | </span>
      <Tooltip :text="storeUser.username">
        <div class="flex gap-0 items-center">
          <div
            class="h-7 flex items-center justify-center w-7 text-base text-black rounded-full bg-white ml-2 mr-[0.5px] z-10 hover:bg-neutral-200"
          >
            {{ storeUser.username[0] }}
          </div>
        </div>
      </Tooltip>
    </div>
  </div>

  <div v-else class="flex gap-4 items-center mr-4">
    <div class="flex flex-row gap-2">
      <a
        href="https://x.com/Fungiball_off"
        target="_blank"
        class="p-2 border flex px-2.5 items-center border-neutral-200 rounded-full hover:cursor-pointer hover:border-white transition-colors"
      >
        <img :src="Twitter" alt="Twitter" class="h-4" />
      </a>

      <a
        href="https://discord.gg/YJJw7RP33K"
        target="_blank"
        class="p-2 border flex items-center border-neutral-200 rounded-full hover:cursor-pointer hover:border-white transition-colors"
      >
        <img :src="Discord" alt="Discord" class="h-5" />
      </a>
    </div>

    <!-- <Button @click="goToPage('SignUp')" size="sm" class="hidden sm:block" type="primary-outline">
      {{ $t("nav.signup") }}
    </Button>

    <Button @click="goToPage('SignIn')" size="sm" class="hidden sm:block" type="primary-white">
      {{ $t("nav.signin") }}
    </Button> -->
    <Button
      @click="storeApp.showAuthModal = true"
      size="sm"
      class="hidden sm:block"
      type="primary-white"
    >
      {{ $t("nav.signin") }}
    </Button>
  </div>
</template>

<style scoped></style>
